/**
 * Deal-wide internal take-off markup PDF export — confidential cover, structure sections with snippets + timber tables.
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { getPublicSettings } from './appSettings';
import type { PdfMeasurement, PdfMeasurementCalibration, PdfTakeoffLink, ProjectFile } from '../types';
import { fetchDealTakeoffLinks } from './pdfTakeoffLinkService';
import { fetchDocumentCalibrations, fetchDocumentMeasurements } from './pdfMeasurementService';
import { fetchDealTakeoffPins } from './pdfTakeoffPinService';
import { formatCrossSectionDims } from './timberTakeoffHelpers';
import {
  clearTakeoffSnippetPdfCache,
  renderStructureSnippets,
  type StructurePageSnippet,
} from './takeoffSnippetRenderer';

const FOREST: [number, number, number] = [44, 62, 47];
const COVER_BG: [number, number, number] = [42, 57, 45];
const COVER_BOX: [number, number, number] = [55, 72, 58];
const MARGIN = 15;

export interface TakeoffExportProgress {
  phase: 'loading' | 'snippets' | 'pdf' | 'done';
  message: string;
  current?: number;
  total?: number;
}

export interface TakeoffExportOptions {
  dealId: string;
  dealTitle?: string;
  contactName?: string;
  userName?: string;
  projectId?: string | null;
  onProgress?: (progress: TakeoffExportProgress) => void;
}

export interface TakeoffExportResult {
  blob: Blob;
  filename: string;
}

interface StructureGroup {
  structureId: string;
  structureName: string;
  links: PdfTakeoffLink[];
}

type JsPdfDoc = import('jspdf').jsPDF & { lastAutoTable?: { finalY: number } };

function slugify(value: string): string {
  return (value || 'takeoff')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function formatMoisture(value: PdfTakeoffLink['moistureContent']): string {
  if (value === 'green') return 'GRN';
  if (value === 'kiln_dried') return 'KD';
  return '—';
}

async function fetchCompanyLogoBase64(): Promise<string | undefined> {
  try {
    const settings = await getPublicSettings();
    if (settings.engagementLogo?.url) {
      const logoUrl = settings.engagementLogo.url.includes('?')
        ? `${settings.engagementLogo.url}&t=${Date.now()}`
        : `${settings.engagementLogo.url}?t=${Date.now()}`;
      const response = await fetch(logoUrl, { mode: 'cors', cache: 'no-store', credentials: 'omit' });
      if (response.ok) {
        const blob = await response.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
      }
    }
  } catch {
    /* fall through */
  }
  try {
    const fallbackResponse = await fetch('/assets/logo/LC_pdf_logo.png');
    if (fallbackResponse.ok) {
      const fallbackBlob = await fallbackResponse.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(fallbackBlob);
      });
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

function documentLabel(
  documentId: string,
  documentUrl: string,
  namesByDocumentId: Map<string, string>,
): string {
  const named = namesByDocumentId.get(documentId);
  if (named) return named;
  try {
    const u = new URL(documentUrl);
    const seg = u.pathname.split('/').pop();
    if (seg) return decodeURIComponent(seg.split('?')[0]);
  } catch {
    /* ignore */
  }
  return documentId.slice(0, 12) || 'Plan';
}

async function fetchProjectFileNames(projectId: string): Promise<Map<string, string>> {
  const q = query(collection(db, 'projectFiles'), where('projectId', '==', projectId));
  const snap = await getDocs(q);
  const map = new Map<string, string>();
  for (const d of snap.docs) {
    const data = d.data() as ProjectFile;
    const name = data.name || 'Plan';
    map.set(d.id, name);
    if (data.url) map.set(data.url, name);
  }
  return map;
}

function groupLinksByStructure(links: PdfTakeoffLink[]): StructureGroup[] {
  const byId = new Map<string, StructureGroup>();
  for (const link of links) {
    let group = byId.get(link.structureId);
    if (!group) {
      group = {
        structureId: link.structureId,
        structureName: link.structureName || 'Structure',
        links: [],
      };
      byId.set(link.structureId, group);
    }
    group.links.push(link);
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.structureName.localeCompare(b.structureName, undefined, { sensitivity: 'base' }),
  );
}

function countSnippetJobs(groups: StructureGroup[]): number {
  let total = 0;
  for (const group of groups) {
    const keys = new Set<string>();
    for (const link of group.links) {
      keys.add(`${link.documentId}:${link.pageNumber}`);
    }
    total += keys.size;
  }
  return total;
}

function drawInternalTakeoffCover(
  doc: JsPdfDoc,
  options: {
    dealTitle?: string;
    contactName?: string;
    userName?: string;
    structureCount: number;
    itemCount: number;
    logoBase64?: string;
  },
): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  doc.setFillColor(...COVER_BG);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  let y = 36;
  if (options.logoBase64) {
    const logoW = 58;
    const logoH = 22;
    doc.addImage(options.logoBase64, 'PNG', (pageWidth - logoW) / 2, y, logoW, logoH, undefined, 'FAST');
    y += logoH + 14;
  }

  const bannerX = 22;
  const bannerW = pageWidth - bannerX * 2;
  const bannerH = 9;
  doc.setFillColor(127, 29, 29);
  doc.setDrawColor(252, 165, 165);
  doc.setLineWidth(0.25);
  doc.roundedRect(bannerX, y, bannerW, bannerH, 1.5, 1.5, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(254, 226, 226);
  doc.text('INTERNAL USE ONLY — DO NOT DISTRIBUTE TO CLIENTS', pageWidth / 2, y + 6, { align: 'center' });
  y += bannerH + 16;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text('Internal Take-off Markup Report', pageWidth / 2, y, { align: 'center' });
  y += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(180, 190, 182);
  doc.text('Plan markup and timber quantities for estimating', pageWidth / 2, y, { align: 'center' });
  y += 20;

  const boxX = 28;
  const boxW = pageWidth - boxX * 2;
  const boxY = y;
  const boxH = options.contactName ? 82 : 74;
  doc.setFillColor(...COVER_BOX);
  doc.rect(boxX, boxY, boxW, boxH, 'F');

  let textY = boxY + 16;
  doc.setTextColor(210, 218, 212);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('PROJECT', pageWidth / 2, textY, { align: 'center' });
  textY += 9;

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(17);
  doc.text(options.dealTitle || 'Untitled Project', pageWidth / 2, textY, { align: 'center' });
  textY += 10;

  if (options.contactName) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(200, 210, 204);
    doc.text(`Client: ${options.contactName}`, pageWidth / 2, textY, { align: 'center' });
    textY += 9;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(170, 180, 174);
  doc.text(`Generated: ${dateStr} at ${timeStr}`, pageWidth / 2, textY, { align: 'center' });
  textY += 8;

  if (options.userName) {
    doc.text(`Exported by ${options.userName}`, pageWidth / 2, textY, { align: 'center' });
    textY += 7;
  }

  doc.text(
    `${options.structureCount} structure${options.structureCount !== 1 ? 's' : ''} · ${options.itemCount} timber item${options.itemCount !== 1 ? 's' : ''}`,
    pageWidth / 2,
    textY,
    { align: 'center' },
  );

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(140, 152, 144);
  doc.text(
    'Confidential — LCTF staff use only. Not formatted for client delivery.',
    pageWidth / 2,
    pageHeight - 18,
    { align: 'center' },
  );
}

function ensureSpace(doc: JsPdfDoc, y: number, needed: number, pageHeight: number): number {
  if (y + needed <= pageHeight - MARGIN) return y;
  doc.addPage();
  return MARGIN + 8;
}

function addSectionHeader(doc: JsPdfDoc, y: number, title: string, pageWidth: number): number {
  doc.setFillColor(...FOREST);
  doc.rect(MARGIN, y - 5, pageWidth - MARGIN * 2, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, MARGIN + 3, y + 2);
  doc.setTextColor(...FOREST);
  return y + 14;
}

function measureSnippetImage(
  snippet: StructurePageSnippet,
  pageWidth: number,
  pageHeight: number,
): { imgW: number; imgH: number } {
  const maxW = pageWidth - MARGIN * 2 - 4;
  const aspect = snippet.heightPx / Math.max(1, snippet.widthPx);
  let imgW = maxW;
  let imgH = imgW * aspect;
  const maxH = pageHeight * 0.52;
  if (imgH > maxH) {
    imgH = maxH;
    imgW = imgH / aspect;
  }
  return { imgW, imgH };
}

/** Caption + image rendered as one block so page breaks never split them. */
function addSnippetFigure(
  doc: JsPdfDoc,
  snippet: StructurePageSnippet,
  caption: string,
  y: number,
  pageWidth: number,
  pageHeight: number,
): number {
  const { imgW, imgH } = measureSnippetImage(snippet, pageWidth, pageHeight);
  const captionH = 7;
  const pad = 2;
  const gap = 2;
  const blockH = pad + imgH + gap + captionH + pad;

  y = ensureSpace(doc, y, blockH + 4, pageHeight);

  const blockX = MARGIN;
  const blockW = pageWidth - MARGIN * 2;
  const blockY = y;
  const imgX = blockX + (blockW - imgW) / 2;

  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(248, 250, 252);
  doc.setLineWidth(0.25);
  doc.roundedRect(blockX, blockY, blockW, blockH, 2, 2, 'FD');

  doc.addImage(
    snippet.imageBytes,
    'JPEG',
    imgX,
    blockY + pad,
    imgW,
    imgH,
    undefined,
    'FAST',
  );

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(caption, blockX + blockW / 2, blockY + pad + imgH + gap + 4.5, { align: 'center' });

  return blockY + blockH + 6;
}

function buildTimberTableRows(links: PdfTakeoffLink[]): string[][] {
  return links
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
    .map(link => [
      link.name || '—',
      link.species || '—',
      link.grade || '—',
      formatMoisture(link.moistureContent),
      formatCrossSectionDims(link.dimensions) || '—',
      String(link.quantity),
    ]);
}

/** Generate a take-off markup PDF for the entire deal. */
export async function exportTakeoffMarkupPdf(
  options: TakeoffExportOptions,
): Promise<TakeoffExportResult> {
  const { dealId, dealTitle, contactName, userName, projectId, onProgress } = options;

  onProgress?.({ phase: 'loading', message: 'Loading take-off items…' });

  const [links, logoBase64] = await Promise.all([
    fetchDealTakeoffLinks(dealId),
    fetchCompanyLogoBase64(),
  ]);

  if (links.length === 0) {
    throw new Error('No take-off items found for this deal');
  }

  const groups = groupLinksByStructure(links);
  const documentIds = Array.from(new Set(links.map(l => l.documentId)));

  const [pins, documentNames, ...docResults] = await Promise.all([
    fetchDealTakeoffPins(dealId),
    projectId ? fetchProjectFileNames(projectId) : Promise.resolve(new Map<string, string>()),
    ...documentIds.map(async id => {
      const [measurements, calibrations] = await Promise.all([
        fetchDocumentMeasurements(id),
        fetchDocumentCalibrations(id),
      ]);
      return { id, measurements, calibrations };
    }),
  ]);

  const measurementsByDocument = new Map<string, PdfMeasurement[]>();
  const calibrationsByDocument = new Map<string, PdfMeasurementCalibration[]>();
  for (const result of docResults) {
    measurementsByDocument.set(result.id, result.measurements);
    calibrationsByDocument.set(result.id, result.calibrations);
  }

  const snippetCtx = {
    measurementsByDocument,
    calibrationsByDocument,
    pins,
  };

  const totalSnippetJobs = countSnippetJobs(groups);
  let snippetDone = 0;
  const snippetsByStructure = new Map<string, StructurePageSnippet[]>();

  clearTakeoffSnippetPdfCache();

  for (const group of groups) {
    onProgress?.({
      phase: 'snippets',
      message: `Rendering ${group.structureName}…`,
      current: snippetDone,
      total: totalSnippetJobs,
    });
    const snippets = await renderStructureSnippets(
      group.structureId,
      group.structureName,
      links,
      snippetCtx,
      () => {
        snippetDone += 1;
        onProgress?.({
          phase: 'snippets',
          message: `Rendering plan snippets (${snippetDone}/${totalSnippetJobs})…`,
          current: snippetDone,
          total: totalSnippetJobs,
        });
      },
    );
    snippetsByStructure.set(group.structureId, snippets);
  }

  onProgress?.({ phase: 'pdf', message: 'Building PDF…' });

  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
    compress: true,
  }) as JsPdfDoc;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  drawInternalTakeoffCover(doc, {
    dealTitle,
    contactName,
    userName,
    structureCount: groups.length,
    itemCount: links.length,
    logoBase64,
  });

  for (const group of groups) {
    doc.addPage();
    let y = MARGIN + 4;
    y = addSectionHeader(doc, y, group.structureName, pageWidth);

    const snippets = snippetsByStructure.get(group.structureId) ?? [];
    for (const snippet of snippets) {
      const planName = documentLabel(snippet.documentId, snippet.documentUrl, documentNames);
      const caption = `${planName} — Page ${snippet.pageNumber}`;
      y = addSnippetFigure(doc, snippet, caption, y, pageWidth, pageHeight);
    }

    y = ensureSpace(doc, y, 40, pageHeight);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...FOREST);
    doc.text('Timber list', MARGIN, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['Name', 'Species', 'Grade', 'Dry', 'Section', 'Qty']],
      body: buildTimberTableRows(group.links),
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: FOREST, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
  }

  onProgress?.({ phase: 'pdf', message: 'Compressing PDF…' });

  const filename = `INTERNAL_TAKEOFF_${slugify(dealTitle || dealId).replace(/-/g, '_').toUpperCase()}_${new Date().toISOString().slice(0, 10)}.pdf`;
  const rawBlob = doc.output('blob') as Blob;
  const blob = await compressPdfBlob(rawBlob);

  clearTakeoffSnippetPdfCache();
  onProgress?.({ phase: 'done', message: 'Export complete' });

  return { blob, filename };
}

/** Re-save PDF with object streams for a smaller download. */
async function compressPdfBlob(blob: Blob): Promise<Blob> {
  try {
    const { PDFDocument } = await import('pdf-lib');
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const compressed = await pdf.save({ useObjectStreams: true });
    return new Blob([compressed.buffer.slice(compressed.byteOffset, compressed.byteOffset + compressed.byteLength)], {
      type: 'application/pdf',
    });
  } catch {
    return blob;
  }
}

/** Trigger browser download of a take-off export blob. */
export function downloadTakeoffExportBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.visibility = 'hidden';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
